// frontend/src/services/cloudinaryService.js
// Cloudinary Image Upload Service
// ============================================
// Handles BOTH product images AND profile pictures
// ============================================

const CLOUDINARY_NAME = process.env.REACT_APP_CLOUDINARY_NAME || 'drcldizc0';
const UPLOAD_PRESET = process.env.REACT_APP_CLOUDINARY_PRESET || 'uniledge_products';
const PROFILE_UPLOAD_PRESET = 'uniledge_profiles'; // Create this in Cloudinary dashboard
const CLOUDINARY_BASE_URL = `https://res.cloudinary.com/${CLOUDINARY_NAME}`;

// ============================================
// PRODUCT IMAGE UPLOADS (Original functionality)
// ============================================

export const uploadToCloudinary = async (imageBlob, folder = 'products') => {
  const formData = new FormData();
  formData.append('file', imageBlob);
  formData.append('upload_preset', UPLOAD_PRESET);
  formData.append('folder', folder);
  
  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData
      }
    );
    
    if (!response.ok) {
      throw new Error('Upload failed');
    }
    
    const data = await response.json();
    
    return {
      publicId: data.public_id,
      url: data.secure_url,
      width: data.width,
      height: data.height,
      format: data.format,
      size: data.bytes
    };
  } catch (error) {
    console.error('Cloudinary upload error:', error);
    throw new Error('Failed to upload image');
  }
};

// Upload all versions and return URLs
export const uploadProductImages = async (processedImage) => {
  const urls = {};
  
  // Upload each version
  for (const [key, versionData] of Object.entries(processedImage.versions)) {
    try {
      const result = await uploadToCloudinary(
        versionData.blob, 
        `products/${key}`
      );
      urls[key] = result.url;
    } catch (error) {
      console.error(`Failed to upload ${key} version:`, error);
      // Fallback to data URL if upload fails
      urls[key] = versionData.dataUrl;
    }
  }
  
  // Upload original
  try {
    const originalResult = await uploadToCloudinary(
      processedImage.original.blob,
      'products/original'
    );
    urls.original = originalResult.url;
  } catch (error) {
    urls.original = processedImage.original.dataUrl;
  }
  
  return urls;
};

// Generate Cloudinary transformation URLs (alternative approach)
export const generateCloudinaryUrls = (baseUrl) => {
  if (!baseUrl) return null;
  
  // Extract base URL parts
  const urlParts = baseUrl.split('/upload/');
  if (urlParts.length !== 2) return null;
  
  const [base, path] = urlParts;
  
  return {
    thumbnail: `${base}/upload/w_200,h_200,c_fill,q_80/${path}`,
    small: `${base}/upload/w_400,h_400,c_fill,q_85/${path}`,
    medium: `${base}/upload/w_800,h_800,c_limit,q_85/${path}`,
    large: `${base}/upload/w_1200,h_1200,c_limit,q_90/${path}`,
    full: `${base}/upload/w_1920,h_1920,c_limit,q_90/${path}`,
    original: baseUrl
  };
};

// ============================================
// PROFILE PICTURE UPLOADS (New functionality)
// ============================================

/**
 * Upload a profile picture to Cloudinary
 * @param {File} file - The image file
 * @param {string} userId - User ID for naming
 * @returns {Promise<Object>} Upload result with multiple size URLs
 */
export const uploadProfilePicture = async (file, userId) => {
  // Validate file
  if (!file) {
    throw new Error('No file provided');
  }

  // Validate file type
  const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  if (!validTypes.includes(file.type)) {
    throw new Error('Invalid file type. Please upload a JPEG, PNG, GIF, or WebP image.');
  }

  // Validate file size (max 5MB)
  const maxSize = 5 * 1024 * 1024;
  if (file.size > maxSize) {
    throw new Error('File too large. Maximum size is 5MB.');
  }

  const timestamp = Date.now();
  const publicId = userId ? `profile_${userId}_${timestamp}` : `profile_${timestamp}`;

  const formData = new FormData();
  formData.append('file', file);
  formData.append('upload_preset', PROFILE_UPLOAD_PRESET);
  formData.append('folder', 'profiles');
  formData.append('public_id', publicId);

  try {
    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${CLOUDINARY_NAME}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error?.message || 'Upload failed');
    }

    const data = await response.json();

    return {
      success: true,
      url: data.secure_url,
      publicId: data.public_id,
      width: data.width,
      height: data.height,
      format: data.format,
      // Generate different size URLs for profiles
      thumbnail: getTransformedUrl(data.public_id, 'c_fill,w_50,h_50,g_face'),
      small: getTransformedUrl(data.public_id, 'c_fill,w_100,h_100,g_face'),
      medium: getTransformedUrl(data.public_id, 'c_fill,w_200,h_200,g_face'),
      large: getTransformedUrl(data.public_id, 'c_fill,w_400,h_400,g_face'),
    };
  } catch (error) {
    console.error('Profile picture upload error:', error);
    return {
      success: false,
      error: error.message || 'Failed to upload profile picture',
    };
  }
};

/**
 * Get a transformed URL for an image
 * @param {string} publicId - The Cloudinary public ID
 * @param {string} transformation - Cloudinary transformation string
 * @returns {string} Transformed image URL
 */
export const getTransformedUrl = (publicId, transformation) => {
  if (!publicId) return null;
  
  // Handle full URLs - extract public ID
  if (publicId.startsWith('http')) {
    const match = publicId.match(/\/upload\/(?:v\d+\/)?(.+)$/);
    if (match) {
      publicId = match[1].replace(/\.[^/.]+$/, '');
    } else {
      return publicId;
    }
  }
  
  return `${CLOUDINARY_BASE_URL}/image/upload/${transformation}/${publicId}`;
};

/**
 * Get profile picture URL with fallback to initials avatar
 * @param {string} url - The profile picture URL
 * @param {string} size - Size: 'thumbnail', 'small', 'medium', 'large'
 * @returns {string} Profile picture URL or default avatar
 */
export const getProfilePictureUrl = (url, size = 'medium') => {
  if (!url) {
    return getDefaultAvatar(size);
  }

  // If it's a Cloudinary URL, apply transformation
  if (url.includes('cloudinary.com')) {
    const sizes = {
      thumbnail: 'c_fill,w_50,h_50,g_face',
      small: 'c_fill,w_100,h_100,g_face',
      medium: 'c_fill,w_200,h_200,g_face',
      large: 'c_fill,w_400,h_400,g_face',
    };
    
    const transformation = sizes[size] || sizes.medium;
    
    // Insert transformation into URL if not already present
    if (!url.includes('/c_fill,') && !url.includes('/w_')) {
      return url.replace('/upload/', `/upload/${transformation}/`);
    }
  }

  return url;
};

/**
 * Get default avatar URL
 * @param {string} size - Size of avatar
 * @returns {string} Default avatar URL
 */
export const getDefaultAvatar = (size = 'medium') => {
  const sizes = {
    thumbnail: 50,
    small: 100,
    medium: 200,
    large: 400,
  };
  
  const dimension = sizes[size] || 200;
  
  return `https://ui-avatars.com/api/?size=${dimension}&background=3B82F6&color=fff&bold=true`;
};

/**
 * Generate avatar with user's initials
 * @param {string} name - User's name
 * @param {string} size - Size of avatar
 * @returns {string} Avatar URL with initials
 */
export const getInitialsAvatar = (name, size = 'medium') => {
  const sizes = {
    thumbnail: 50,
    small: 100,
    medium: 200,
    large: 400,
  };
  
  const dimension = sizes[size] || 200;
  const initials = name
    ? name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : 'U';
  
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(initials)}&size=${dimension}&background=3B82F6&color=fff&bold=true`;
};

// ============================================
// DEFAULT EXPORT
// ============================================

export default {
  // Product uploads
  uploadToCloudinary,
  uploadProductImages,
  generateCloudinaryUrls,
  // Profile uploads
  uploadProfilePicture,
  getTransformedUrl,
  getProfilePictureUrl,
  getDefaultAvatar,
  getInitialsAvatar,
};